import React from 'react';
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, Clock, Sparkles, Star } from 'lucide-react';
import Avatar from '../Avatar';
import { ptsFor } from '../../utils/points';
import { relativeDay } from '../../utils/relativeDay';

const HistoryList = ({
  filteredSortedLogs, pageLogs, page, setPage, totalPages,
  sortField, setSortField, sortOrder, setSortOrder,
  historySearch, setHistorySearch, loading, onSelectLog,
}) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-mono font-medium text-ink-fg/60 uppercase tracking-wider flex items-center gap-1.5">
          Full History
          <span className="text-[10px] bg-ink-card2 text-ink-fg/40 px-1.5 py-0.5 rounded-full font-semibold">
            {filteredSortedLogs.length}
          </span>
        </h3>

        <div className="flex items-center gap-2">
          {/* Sort Toggle */}
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="text-[10px] font-bold text-ink-fg/60 bg-ink-card2 border-none rounded-lg px-2 py-1 outline-none cursor-pointer hover:bg-ink-card3 transition-colors"
          >
            <option value="doneAt">Date</option>
            <option value="choreTitle">Name</option>
            <option value="actualDifficulty">Difficulty</option>
            <option value="actualMinutes">Time</option>
          </select>
          <button
            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="p-1 text-ink-fg/40 hover:text-mint transition-colors"
            title="Toggle Sort Order"
          >
            <ArrowUpDown size={14} />
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-fg/35" />
        <input
          type="text"
          placeholder="Search by chore or roommate..."
          value={historySearch}
          onChange={(e) => setHistorySearch(e.target.value)}
          className="w-full bg-ink-card3 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-xs text-ink-fg/80 focus:bg-ink-card2 focus:ring-1 focus:ring-mint outline-none transition-all placeholder:text-ink-fg/30"
        />
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 rounded-2xl skeleton-shimmer" style={{ animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
      ) : pageLogs.length === 0 ? (
        <p className="text-ink-fg/40 italic text-sm text-center py-8">
          No chores logged in this period.
        </p>
      ) : (
        <div className="space-y-2">
          {pageLogs.map(log => {
            const names = log.user.map(u => u.name).join(', ') || 'Someone';
            const avgGrade = log.othersUsersGrade.length
              ? (log.othersUsersGrade.reduce((s, g) => s + g.grade, 0) / log.othersUsersGrade.length).toFixed(1)
              : null;
            return (
              <div
                key={log.id}
                onClick={() => onSelectLog(log)}
                className="bg-ink-card border border-white/10 rounded-2xl p-4 shadow-sm flex items-center gap-3 cursor-pointer hover:shadow-md hover:border-mint/25 transition-all active:scale-[0.98]"
              >
                <Avatar user={log.user?.[0]} imgBorder="border border-white/10" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink-fg text-sm truncate">"{log.choreTitle}"</p>
                  <p className="text-xs text-ink-fg/50 mt-0.5 truncate">
                    <span className="font-medium text-mint">{names}</span>
                    <span className="text-ink-fg/30"> · </span>
                    {relativeDay(log.doneAt)}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-[11px] font-mono text-ink-fg/40 flex items-center gap-1">
                      <Clock size={10} /> {log.actualMinutes} min
                    </span>
                    <span className="text-[11px] font-mono text-mint flex items-center gap-1">
                      <Sparkles size={10} /> +{ptsFor(log.actualMinutes, log.actualDifficulty)}
                    </span>
                    {avgGrade && (
                      <span className="text-[11px] font-mono text-amber-300 flex items-center gap-1">
                        <Star size={10} className="fill-amber-400" /> {avgGrade}/5
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="p-2 rounded-xl border border-white/10 text-ink-fg/50 disabled:opacity-30 hover:bg-ink-card3 transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs font-mono text-ink-fg/40 font-medium">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            className="p-2 rounded-xl border border-white/10 text-ink-fg/50 disabled:opacity-30 hover:bg-ink-card3 transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoryList;
